import { useAuthStore } from '@/features/auth/auth-store';

import { RoleSwitcher } from './RoleSwitcher';
import { ThemeToggle } from './ThemeToggle';

/**
 * Top navigation bar — fixed-height brand banner across the top of the
 * AppShell, restyled to Ryan's v6 header (SCRUM-321):
 *
 *   - Left: three-square logo + wordmark, and the active-role chip (gold mono
 *     pill with chevron). The chip is NON-interactive — the prototype's
 *     role-switcher dropdown is deferred until real role claims land
 *     (SCRUM-7 / SCRUM-103 / SCRUM-290).
 *   - Right: the v6 pill theme toggle and a 32px user-initials avatar.
 *
 * Removed from the pre-v6 header (per design): the notifications bell and the
 * top-right sign-out icon — Sign Out lives in the SideNav footer rail
 * (SCRUM-298), which remains the single sign-out affordance.
 */
export function TopNav() {
  const user = useAuthStore((s) => s.user);

  return (
    <nav
      aria-label="Primary"
      className="bg-bg-4 border-border-0 px-tk-xl flex h-14 flex-shrink-0 items-center justify-between border-b"
    >
      <div className="gap-tk-xl flex items-center">
        {/* Logo: three-square mark from Ryan's prototype.
         * Rendered as a button (not an <a>) because the scaffold has no real
         * home route to navigate to yet. Real routing arrives in SCRUM-83. */}
        <button
          type="button"
          className="gap-tk-sm flex items-center bg-transparent"
          aria-label="BankIQ home"
        >
          <svg
            width="28"
            height="28"
            viewBox="20 20 62 62"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect
              x="51.875"
              y="24"
              width="26.125"
              height="26.125"
              rx="3"
              fill="var(--cyan)"
              fillOpacity="0.3"
            />
            <rect
              x="24"
              y="51.875"
              width="26.125"
              height="26.125"
              rx="3"
              fill="var(--cyan)"
              fillOpacity="0.9"
            />
            <rect
              x="37.47"
              y="37.937"
              width="26.125"
              height="26.125"
              rx="3"
              fill="var(--cyan)"
              fillOpacity="0.6"
            />
          </svg>
          <span className="font-display text-text-0 text-lg font-bold tracking-tight">
            Bank<span className="text-brand-primary">IQ</span>
          </span>
        </button>

        <RoleSwitcher />
      </div>

      <div className="gap-tk-sm flex items-center">
        <ThemeToggle />

        {/* User avatar — v6 initials circle. Visual affordance only in Phase 1
            (no menu); a full user menu arrives with OIDC user claims in
            SCRUM-7 / SCRUM-102. Sign Out lives in the SideNav footer. */}
        <div
          aria-label="User menu"
          className="bg-brand-dim text-brand-primary rounded-tk-full flex h-8 w-8 items-center justify-center border border-[var(--border-accent)] text-xs font-semibold"
        >
          {initialsOf(user?.displayName)}
        </div>
      </div>
    </nav>
  );
}

/**
 * First letters of the first two words of the display name (v6 avatar
 * convention, e.g. "Ryan Lynch" → "RL"). Falls back to "BQ" (BankIQ) when no
 * user is present — only reachable in tests, since the AppShell renders
 * behind the auth gate.
 */
function initialsOf(displayName: string | undefined): string {
  if (!displayName) return 'BQ';
  const parts = displayName
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts[1]?.charAt(0) ?? '';
  return (first + second).toUpperCase() || 'BQ';
}
